import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Star, ArrowRight, Sparkles } from "lucide-react";
import { LOGO_URL } from "@/constants/assets";
import { SEASONAL } from "@/constants/seasonal";

const bubbles = [
  { size: 18, left: "8%", delay: "0s", dur: "9s" },
  { size: 10, left: "16%", delay: "2.5s", dur: "11s" },
  { size: 24, left: "26%", delay: "1.2s", dur: "10s" },
  { size: 12, left: "72%", delay: "0.8s", dur: "12s" },
  { size: 20, left: "82%", delay: "3s", dur: "9.5s" },
  { size: 8, left: "90%", delay: "1.8s", dur: "8.5s" },
  { size: 14, left: "55%", delay: "4s", dur: "13s" },
];

const KineticLine = ({ words, delay, accent, className }) => (
  <span className={`flex flex-wrap gap-x-[0.28em] ${className || ""}`}>
    {words.map((w, i) => (
      <span key={i} className="overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
          className={`inline-block ${accent ? "font-display italic font-medium text-[#990000]" : ""}`}
          initial={{ y: "115%", rotate: 4 }}
          animate={{ y: 0, rotate: 0 }}
          transition={{ delay: delay + i * 0.12, duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
        >
          {w}
        </motion.span>
      </span>
    ))}
  </span>
);

const Hero = () => {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 16 });
  const onMove = (e) => {
    const r = sectionRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={sectionRef} onMouseMove={onMove} onMouseLeave={onLeave} id="top" className="hero-grain relative overflow-hidden pt-24 md:pt-28 pb-16">
      {bubbles.map((b, i) => (
        <span key={i} className="bubble" style={{ width: b.size, height: b.size, left: b.left, animationDelay: b.delay, animationDuration: b.dur }} />
      ))}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-6 items-center">
        <div>
          <h1 data-testid="hero-kinetic-title" className="font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl flex flex-col">
            {SEASONAL.headline.map((line, i) => (
              <KineticLine key={i} words={line.words} delay={0.15 + i * 0.3} accent={line.accent} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.6 }}
            className="mt-6 text-base md:text-lg text-[#5C5955] max-w-xl leading-relaxed"
          >
            Tidy Tusks brings meticulous, white-glove cleaning to homes and offices across Northwest Arkansas — scrubbing every corner with the tenacity of a Razorback.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a href="#quote" data-testid="hero-cta-quote-btn" className="animate-pulse-ring inline-flex items-center gap-2 bg-[#990000] text-white font-semibold py-3.5 px-7 rounded-full shadow-md hover:bg-[#7A0000] hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
              Get My Free Quote <ArrowRight size={17} />
            </a>
            <a href="#before-after" data-testid="hero-cta-work-btn" className="inline-flex items-center gap-2 border-2 border-[#1A1A1A] text-[#1A1A1A] font-semibold py-3 px-7 rounded-full hover:bg-[#1A1A1A] hover:text-white transition-all duration-200">
              See Our Work
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats-row"
          >
            {[
              { top: "4.9", sub: "Average rating", star: true },
              { top: "250+", sub: "Homes cleaned" },
              { top: "100%", sub: "Satisfaction guarantee" },
            ].map((s, i) => (
              <div key={i}>
                <div className="font-display text-2xl md:text-3xl font-bold flex items-center gap-1.5">
                  {s.top} {s.star && <Star size={18} className="fill-[#990000] text-[#990000]" />}
                </div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#5C5955] mt-1">{s.sub}</div>
              </div>
            ))}
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{ rotateX, rotateY, transformPerspective: 1000 }}
          className="relative mx-auto w-full max-w-md lg:max-w-none will-change-transform"
        >
          <div className="absolute -inset-6 bg-[#F3EDE4] rounded-[3rem] rotate-3" />
          <div className="animate-float-y relative bg-white border border-[#E5DEC9] rounded-[2.5rem] p-8 md:p-10 shadow-xl">
            <img src={LOGO_URL} alt="Tidy Tusks mop and tusks emblem" data-testid="hero-logo-image" className="w-full h-auto object-contain" />
          </div>
          <div className="absolute -bottom-5 -left-4 md:-left-8 bg-[#1A1A1A] text-white rounded-2xl px-5 py-3.5 shadow-2xl flex items-center gap-3 rotate-[-3deg]" data-testid="hero-rating-badge">
            <div className="animate-spin-slow text-[#D4AF37]"><Star size={22} className="fill-[#D4AF37]" /></div>
            <div>
              <div className="font-display font-bold text-lg leading-none">4.9 Rated</div>
              <div className="text-[11px] text-white/70 mt-1">250+ homes across NWA</div>
            </div>
          </div>
          <div className="absolute -top-4 -right-3 md:-right-6 bg-[#990000] text-white rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-[0.14em] shadow-lg rotate-[4deg] flex items-center gap-1.5" data-testid="hero-local-badge">
            <Sparkles size={14} /> Locally Owned · NWA
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
